<template>
  <div class="otp-input-container">
    <label v-if="label" :for="`otp-input-0`" class="otp-label">
      {{ label }}
    </label>
    <div class="otp-inputs-wrapper">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        :ref="(el) => (inputRefs[index] = el)"
        :id="`otp-input-${index}`"
        v-model="digits[index]"
        type="text"
        inputmode="numeric"
        maxlength="1"
        :class="['otp-input', { 'otp-input-error': error, 'otp-input-filled': digits[index] }]"
        :disabled="disabled"
        :placeholder="placeholder"
        @input="handleInput(index, $event)"
        @keydown="handleKeyDown(index, $event)"
        @paste="handlePaste($event)"
        @focus="handleFocus(index)"
      />
    </div>
    <small v-if="error && errorMessage" class="otp-error-message">
      {{ errorMessage }}
    </small>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from "vue";

const props = defineProps({
  value: {
    type: String,
    default: "",
  },
  modelValue: {
    type: String,
    default: "",
  },
  length: {
    type: Number,
    default: 4,
    validator: (value) => value > 0 && value <= 10,
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  autoFocus: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["input", "update:modelValue", "complete"]);

const digits = ref(Array(props.length).fill(""));
const inputRefs = ref([]);

const handleInput = (index, event) => {
  const value = event.target.value;

  if (!/^\d*$/.test(value)) {
    digits.value[index] = "";
    emitValue();
    return;
  }

  if (value) {
    digits.value[index] = value;

    if (index < props.length - 1) {
      nextTick(() => {
        inputRefs.value[index + 1]?.focus();
      });
    }
  } else {
    // Cuando se borra el valor, limpiar el dígito
    digits.value[index] = "";
  }

  emitValue();
};

const handleKeyDown = (index, event) => {
  if (event.key === "Backspace") {
    if (!digits.value[index] && index > 0) {
      // Si el campo actual está vacío, mover al anterior
      nextTick(() => {
        inputRefs.value[index - 1]?.focus();
      });
    } else if (digits.value[index]) {
      // Si el campo tiene contenido, se limpiará por el evento input
      // Solo necesitamos asegurarnos de que se emita el valor después
      nextTick(() => {
        emitValue();
      });
    }
  }

  if (event.key === "ArrowLeft" && index > 0) {
    event.preventDefault();
    inputRefs.value[index - 1]?.focus();
  }

  if (event.key === "ArrowRight" && index < props.length - 1) {
    event.preventDefault();
    inputRefs.value[index + 1]?.focus();
  }
};

const handlePaste = (event) => {
  event.preventDefault();
  const pastedData = event.clipboardData.getData("text").trim();

  if (!/^\d+$/.test(pastedData)) {
    return;
  }

  const pastedDigits = pastedData.slice(0, props.length).split("");
  pastedDigits.forEach((digit, index) => {
    if (index < props.length) {
      digits.value[index] = digit;
    }
  });

  nextTick(() => {
    inputRefs.value.forEach((input, index) => {
      if (input && digits.value[index]) {
        input.value = digits.value[index];
      }
    });

    const lastFilledIndex = Math.min(pastedDigits.length - 1, props.length - 1);
    inputRefs.value[lastFilledIndex]?.focus();
  });

  emitValue();
};

const handleFocus = (index) => {
  nextTick(() => {
    inputRefs.value[index]?.select();
  });
};

const emitValue = () => {
  const value = digits.value.join("");
  // Emitir ambos eventos para compatibilidad con Vue 2 y Vue 3
  emit("input", value);
  emit("update:modelValue", value);

  if (value.length === props.length) {
    emit("complete", value);
  }
};

const clear = () => {
  digits.value = Array(props.length).fill("");
  emitValue();
  focusFirst();
};

const focusFirst = () => {
  nextTick(() => {
    inputRefs.value[0]?.focus();
  });
};

// Observar ambas props para compatibilidad Vue 2 y Vue 3
watch(
  () => props.value || props.modelValue,
  (newValue) => {
    if (newValue !== digits.value.join("")) {
      const newDigits = newValue.slice(0, props.length).split("");
      digits.value = [...newDigits, ...Array(props.length - newDigits.length).fill("")];
    }
  }
);

onMounted(() => {
  if (props.autoFocus) {
    focusFirst();
  }
});

defineExpose({
  clear,
  focusFirst,
});
</script>

<style scoped>
.otp-input-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.otp-label {
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  font-family: "DM Sans", sans-serif;
  margin-bottom: 0;
}

.otp-inputs-wrapper {
  display: flex;
  gap: 12px;
  justify-content: start;
}

.otp-input {
  width: 56px;
  height: 64px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  font-family: "DM Sans", sans-serif;
  color: #ffffff;
  background-color: transparent;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  transition: all 0.2s ease;
  outline: none;
}

.otp-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
  font-size: 32px;
}

.otp-input:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.6);
}

.otp-input:focus {
  border-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.15);
}

.otp-input-filled {
  border-color: rgba(255, 255, 255, 0.7);
  background-color: rgba(255, 255, 255, 0.05);
}

.otp-input-error {
  border-color: #ff6b6b !important;
}

.otp-input-error:focus {
  box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.2);
}

.otp-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: rgba(255, 255, 255, 0.05);
}

.otp-error-message {
  color: #ffb3b3;
  font-size: 13px;
  font-family: "DM Sans", sans-serif;
  text-align: center;
  margin: 0;
}

@media (max-width: 480px) {
  .otp-input {
    width: 48px;
    height: 56px;
    font-size: 20px;
  }

  .otp-inputs-wrapper {
    gap: 8px;
  }
}
</style>
